/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import styled from "styled-components";
import { AppHeader } from "components/shared/AppHeader";
import { AppFooter } from "components/shared/AppFooter";
import {
  HomeColumn,
  Splash,
  SplashBackdrop,
  SplashCopy,
  SplashIllustration,
  Subtitle,
  Title,
} from "components/shared/Splash";
import { JobsLineup } from "components/LandingPage/JobsLineup";
import { Feature } from "components/LandingPage/Feature";
import { StudioHero } from "components/LandingPage/StudioHero";

import micCheck2x from "images/home/landing/featurehighlights/mic-check-home@2x.jpg?responsive";
import music2x from "images/home/landing/featurehighlights/music-home@2x.jpg?responsive";
import recording2x from "images/home/landing/featurehighlights/recording-home@2x.jpg?responsive";
import switch2x from "images/home/landing/featurehighlights/switch-home@2x.jpg?responsive";
import transcript2x from "images/home/landing/featurehighlights/transcript-home@2x.jpg?responsive";
import { TemplateLineup } from "components/LandingPage/TemplateLineup";

const LandingPage: React.FC = () => {
  return (
    <>
      <PageContent>
        <AppHeader />

        <LandingSplash />
        <HomeColumn>
          <JobsLineup />
        </HomeColumn>

        <StudioHero />

        <TemplateLineup />

        <HomeColumn>
          <Feature
            image={switch2x}
            title={I18n.t("landing.features.enhance.title")}
            body={I18n.t("landing.features.enhance.body")}
          />

          <Feature
            image={micCheck2x}
            title={I18n.t("landing.features.mic_check.title")}
            body={I18n.t("landing.features.mic_check.body")}
          />

          <Feature
            image={transcript2x}
            title={I18n.t("landing.features.transcript.title")}
            body={I18n.t("landing.features.transcript.body")}
          />

          <Feature
            image={recording2x}
            title={I18n.t("landing.features.recording.title")}
            body={I18n.t("landing.features.recording.body")}
          />

          <Feature
            image={music2x}
            title={I18n.t("landing.features.music.title")}
            body={I18n.t("landing.features.music.body")}
          />
        </HomeColumn>
        <AppFooter />
      </PageContent>
    </>
  );
};

const LandingSplash: React.FC = () => {
  return (
    <>
      <SplashBackdrop>
        <Splash>
          <SplashCopy>
            <Title>{I18n.t("landing.title")}</Title>
            <Subtitle>{I18n.t("landing.subtitle")}</Subtitle>
          </SplashCopy>
          <SplashIllustration />
        </Splash>
      </SplashBackdrop>
    </>
  );
};

const PageContent = styled.div`
  overflow-x: hidden;
  background-color: var(--spectrum-global-color-static-white);
  position: relative;
`;

export default LandingPage;
